/**
 * Desktop preferences persistence — LOCAL ONLY.
 *
 * The source app synced desktop preferences to a backend. This portfolio build
 * has no backend, so preferences live purely in scoped localStorage. The
 * backend-facing functions are kept as no-op / null-returning stubs so the
 * consuming hook (`useDesktopPreferences`) keeps its original shape and can be
 * re-pointed at a real backend later without changing its call sites.
 */
import type {
  DesktopPreferences,
  SaveDesktopPreferencesRequest,
} from "@repo/types";
import { scopedKey } from "../lib/scopedStorage";

const STORAGE_KEY_BASE = "desktop-web-shell:iconPreferences";
const storageKey = () => scopedKey(STORAGE_KEY_BASE);

/** Load preferences from localStorage. */
export function loadLocalPreferences(): DesktopPreferences | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Save preferences to localStorage. */
export function saveLocalPreferences(prefs: DesktopPreferences): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(prefs));
  } catch {
    console.warn("Failed to save desktop preferences to localStorage");
  }
}

/** Clear localStorage preferences. */
export function clearLocalPreferences(): void {
  try {
    localStorage.removeItem(storageKey());
  } catch {
    // ignore
  }
}

/**
 * Schedule a persisted save. With no backend, the authoritative write already
 * happened synchronously via `saveLocalPreferences`, so this is a no-op kept
 * for API compatibility.
 */
export function scheduleSavePreferences(
  _payload: SaveDesktopPreferencesRequest
): void {
  // no-op (local-only build)
}

/** Guest-mode toggle was a backend-sync concern; no-op here. */
export function setPreferencesGuestMode(_guest: boolean): void {
  // no-op (local-only build)
}

/** There is no backend to sync from. */
export async function syncPreferencesFromBackend(): Promise<DesktopPreferences | null> {
  return null;
}

/** There is no backend to force-save to. */
export async function forceSavePreferences(): Promise<void> {
  // no-op (local-only build)
}

/** There is no backend to repair. */
export async function repairPreferencesOnBackend(
  _payload: SaveDesktopPreferencesRequest
): Promise<void> {
  // no-op (local-only build)
}

/** No pending backend flush on page hide in a local-only build. */
export function flushPreferencesOnPageHide(): void {
  // no-op (local-only build)
}
